<template>
  <div id="app" class="flex flex-col h-screen">
    <welcome-offer v-if="welcomeBannerText" :text="welcomeBannerText" />
    <div
      class="bg-center bg-no-repeat bg-cover"
      style="
        background-image: url(/app/assets/images/fond3dsmax.png);
        min-height: 126px;
      "
    >
      <app-menu
        v-if="ready"
        :user-alert="userAlert"
        :has-promotion="hasPromotion"
        :i18n="menuData.i18n"
      />
      <offline-menu
        v-else-if="loginData"
        :i18n="loginData.i18n.menu"
        :admin-menu="loginData.admin_menu"
      />
    </div>
    <div
      id="content"
      class="bg-white flex-grow flex flex-col justify-start"
      v-if="loginData"
    >
      <div class="flex-grow flex flex-col justify-between">
        <section class="container mx-auto py-12">
          <LoginOrRegister
            v-if="loginData"
            :i18n="loginData"
            @logged-in="loadInitialData"
          />
        </section>
      </div>
    </div>
    <div class="flex-1 md:overflow-y-hidden" ref="main" v-else-if="ready">
      <keep-alive>
        <router-view />
      </keep-alive>
    </div>
    <modals-container :locale="locale"></modals-container>

    <flash :messages="[]"></flash>
  </div>
</template>

<script>
import { setUser as setUserSentry } from "@sentry/browser";
import axios from "axios";
import { mapGetters } from "vuex";

import {
  dispatchGoogleAnalyticsEvents,
  setErrorTranslations,
  handleAxiosError,
} from "@/boot/functions";

import "./style/app.scss";

import EnquiryForms from "./components/Support/EnquiryForms";

export default {
  components: {
    "welcome-offer": require("./components/WelcomeOffer").default,
    "app-menu": require("./components/Menu").default,
    "offline-menu": require("./components/OfflineMenu").default,
    flash: require("./components/Commons/Flash/Flash").default,
    LoginOrRegister: require("./components/Auth/LoginOrRegister").default,
  },
  created() {
    this.forceRefreshAt = Date.now() + 1000 * 60 * 60 * 24; // force refresh after 24h
    window.addEventListener("beforeunload", this.leaving);

    // intercept all responses
    axios.interceptors.response.use(
      (response) => {
        if (response.headers) {
          this.interceptHeaders(response.headers);
        }
        return response;
      },
      (error) => {
        if (error.response && 401 === error.response.status) {
          const data = error.response.data;
          this.loginData = data;
          this.$store.commit("app/setI18nComponents", data.i18n.components); // used by LocaleSwitcher
        }
        if (error.response && error.response.headers) {
          this.interceptHeaders(error.response.headers);
        }
        return Promise.reject(error);
      }
    );

    // intercept all requests
    axios.interceptors.request.use(
      (config) => {
        config.headers["X-RC-Locale"] = this.$route.params.locale;
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.loadInitialData();
  },
  data() {
    return {
      ready: false,
      titleTranslation: {},
      userAlert: null,
      hasPromotion: false,
      loginData: false,
      menuData: {},
    };
  },
  methods: {
    interceptHeaders(headers) {
      if (headers["x-rc-alert-type"] === undefined) {
        return;
      } else if (headers["x-rc-alert-type"]) {
        this.userAlert = {
          type: headers["x-rc-alert-type"],
          msg: decodeURIComponent(headers["x-rc-alert-msg"]),
        };
      } else {
        this.userAlert = null;
      }
      this.hasPromotion = decodeURIComponent(headers["x-rc-promotion"]);
    },
    loadInitialData() {
      return axios
        .get("/api/app/initial-data")
        .then((r) => {
          this.loginData = false;
          return this.setInitialState(r.data.data);
        })
        .catch(handleAxiosError);
    },
    setInitialState(initialData) {
      this.titleTranslation = initialData.i18n.menu;

      setUserSentry({ id: initialData.user.id });

      this.$store.commit("account/setUser", initialData.user);
      this.$store.commit("account/setSafe", initialData.user.safe);
      this.$store.commit("account/setUsername", initialData.user.name);
      this.$store.commit("account/setContact", initialData.user.contact);
      this.$store.commit(
        "account/setPreferences",
        initialData.user.preferences
      );
      this.$store.commit("account/setTransfer", initialData.user.transfer);
      this.$store.commit("account/setId", initialData.user.id);
      this.$store.commit("account/setI18n", initialData.i18n.user);
      this.$store.commit("account/setRoutes", initialData.user.routes);
      this.$store.commit(
        "account/setMiscellaneous",
        initialData.user.miscellaneous
      );
      this.$store.commit(
        "account/setProjectsCount",
        initialData.user.projectsCount
      );
      this.$store.commit(
        "account/setRefillsCount",
        initialData.user.refillsCount
      );
      this.$store.commit("app/setI18nTracker", initialData.i18n.tracker);
      this.$store.commit("app/setI18nComponents", initialData.i18n.components);
      this.$store.commit("app/setApplications", initialData.applications);
      this.$store.commit("app/setLastRenderer", initialData.lastRenderer);
      this.$store.commit("app/setRanchsync", initialData.ranchsync);
      this.$store.commit("app/setI18nErrors", initialData.i18n.errors);
      this.$store.commit("app/setRoutes", initialData.routes);
      this.$store.commit(
        "dashboard/setTranslations",
        initialData.i18n.dashboard
      );
      this.$store.commit(
        "dashboard/setProjectsRoutes",
        initialData.routes.projects
      );
      this.$store.commit(
        "dashboard/setProjectsStatuses",
        initialData.projects.statuses
      );
      this.$store.commit(
        "dashboard/setRefreshRates",
        initialData.projects.refreshRates
      );
      this.$store.commit(
        "formsData/setUserActivities",
        initialData.userActivities
      );
      this.$store.commit("formsData/setCountries", initialData.countries);
      this.menuData.i18n = initialData.i18n.menu;
      this.$store.commit("safe/setForm", initialData.user.contact);
      this.$store.commit("safe/setTranslations", initialData.i18n.safe);
      this.$store.commit("safe/setRoutes", initialData.routes.safe);
      this.$store.commit("queue/setI18n", initialData.i18n.queue);
      this.$store.commit("queue/setRefreshRate", initialData.queue.refreshRate);
      this.$store.commit("upload/setI18n", initialData.i18n.upload);
      this.$store.commit("support/setTranslations", initialData.i18n.support);

      // This has to stay at the end because it ensures that all app data are ready
      // TODO : maybe make it on store modules level to have mor granularity
      this.$store.commit("app/setReady", true);
      this.ready = true;
      dispatchGoogleAnalyticsEvents(initialData);
      setErrorTranslations(initialData.i18n.errors);

      if (this.$route.query.supportModal) {
        this.showSupportModal();
      }

      if (initialData.frontChatInit) {
        let frontChatScript = document.createElement("script");
        frontChatScript.onload = function () {
          console.log("loaded");
          window.FrontChat("init", initialData.frontChatInit);
        };
        frontChatScript.setAttribute(
          "src",
          "https://chat-assets.frontapp.com/v1/chat.bundle.js"
        );
        document.head.appendChild(frontChatScript);
      }
    },
    leaving(e) {
      if (this.hasUploadingFiles) {
        let confirmationMessage = this.$store.state.upload.i18n.alerts.leaving;
        e.returnValue = confirmationMessage; // Gecko, Trident, Chrome 34+
        return confirmationMessage; // Not working in most navigators.
      }
    },
    showSupportModal() {
      this.$modal.show(
        EnquiryForms,
        {},
        {
          name: "general_support_modal",
          width: 750,
          height: "auto",
          //maxWidth: 900,
          scrollable: true,
          pivotY: 0.5,
          pivotX: 0.5,
          clickToClose: false,
        }
      );
    },
  },
  computed: {
    ...mapGetters({
      hasRunningAttempts: "tuspload/hasRunningAttempts",
    }),
    hasUploadingFiles() {
      return this.hasRunningAttempts.length > 0;
    },
    locale() {
      return this.$route.params.locale || "en";
    },
    welcomeBannerText() {
      if (this.ready) {
        if (this.$store.state.account.projectsCount <= 10) {
          return this.menuData.i18n.welcome_offer_banner;
        }
        return ""; // experienced user: hide banner
      }
      if (!this.loginData) {
        return " "; // loading state (show empty banner)
      }
      return this.loginData.i18n.menu.welcome_offer_banner;
    },
  },

  watch: {
    $route: {
      // eslint-disable-next-line no-unused-vars
      handler(to, from) {
        // force refresh after 24h
        if (!this.hasUploadingFiles && Date.now() > this.forceRefreshAt) {
          window.location.href = to.fullPath;
        }
        const title = this.titleTranslation[to.meta.titleKey] || "";
        if (title) {
          document.title = title + " - Ranch Computing";
        } else {
          document.title = "Ranch Computing";
        }
        if (to.query.supportModal) {
          this.showSupportModal();
        } else if (from && from.query.supportModal) {
          this.$modal.hide("general_support_modal");
        }
      },
      immediate: true,
    },
  },
};
</script>
